import { useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router';
import { Pennant } from 'markcat-ui';
import { useAuthStore } from '../stores/auth';
import { apiFetch, ApiError, friendlyErrorMessage } from 'markcat-api-client';
import type { User } from 'markcat-domain';
import styles from './Login.module.css';
import { usePageTitle } from '../hooks/usePageTitle';

interface AuthResponse {
  access_token: string;
  // refresh_token is delivered via HttpOnly cookie in cloud mode; the body
  // field is still emitted for the extension and local-mode clients.
  refresh_token?: string;
  user: User;
}

export function Login() {
  // Pricing CTAs arrive with ?plan=<key>. After successful auth we forward the
  // intent to the Billing page which auto-opens Stripe Checkout for that plan.
  // Landing signup CTAs arrive with ?mode=register.
  const [searchParams] = useSearchParams();
  const planIntent = searchParams.get('plan');
  // Set by AuthGuard when an authenticated route was hit while logged out
  // (e.g. a shared link landing on /app/share). Return the user there post-auth.
  // Only follow internal absolute paths: reject scheme-relative ('//evil.com')
  // and anything not rooted at '/', so a crafted ?next= can't send a fresh
  // login anywhere but into the app.
  const rawNext = searchParams.get('next');
  const next = rawNext && rawNext.startsWith('/') && !rawNext.startsWith('//') ? rawNext : null;

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isRegister, setIsRegister] = useState(searchParams.get('mode') === 'register');
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const setAuth = useAuthStore((s) => s.setAuth);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const navigate = useNavigate();
  usePageTitle(isRegister ? 'Create account — Markcat' : 'Sign in — Markcat');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (forgotMode) {
        await apiFetch('/api/auth/forgot-password', {
          method: 'POST',
          body: JSON.stringify({ email }),
        });
        setForgotSent(true);
        setLoading(false);
        return;
      }

      const endpoint = isRegister ? '/api/auth/register' : '/api/auth/login';
      const res = await apiFetch<AuthResponse>(endpoint, {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      });
      setAuth(res.user, res.access_token);
      if (next) {
        // searchParams already decoded the value once; it is the exact
        // path+search AuthGuard preserved. Navigate to it verbatim.
        navigate(next, { replace: true });
      } else if (planIntent) {
        navigate(`/app/billing?checkout=${encodeURIComponent(planIntent)}`);
      } else {
        navigate('/app');
      }
    } catch (err: unknown) {
      // A login 401 carries the real reason ("Invalid email or password") in
      // err.message; userMessage would mislabel it "Session expired".
      setError(
        err instanceof ApiError && err.status === 401 ? err.message : friendlyErrorMessage(err),
      );
    } finally {
      setLoading(false);
    }
  };

  // Already signed in (persisted token): skip the form, preserve plan/next intent.
  // A stale token is fine — /app's SessionBootstrap refreshes or bounces back here.
  if (isAuthenticated) {
    const dest = next ?? (planIntent ? `/app/billing?checkout=${encodeURIComponent(planIntent)}` : '/app');
    return <Navigate to={dest} replace />;
  }

  if (forgotMode) {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <Pennant size={40} className={styles.mark} />
          <h2 className={styles.title}>Reset password</h2>
          {forgotSent ? (
            <p className={styles.subtitle}>If the email exists, a reset link has been sent.</p>
          ) : (
            <>
              {error && <p className={styles.error} role="alert">{error}</p>}
              <form className={styles.form} onSubmit={handleSubmit}>
                <input
                  type="email" name="email" id="email" placeholder="Email" aria-label="Email" className={styles.input}
                  autoComplete="email"
                  value={email} onChange={(e) => setEmail(e.target.value)} required
                />
                <button type="submit" className={styles.button} disabled={loading} aria-busy={loading}>
                  {loading ? <><span className="mc-spinner" aria-hidden />Sending…</> : 'Send reset link'}
                </button>
              </form>
            </>
          )}
          <p className={styles.footer}>
            <button className={styles.toggle} onClick={() => { setForgotMode(false); setForgotSent(false); setError(''); }}>
              Back to sign in
            </button>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <Pennant size={40} className={styles.mark} />
        <h2 className={styles.title}>{isRegister ? 'Create account' : 'Sign in to Markcat'}</h2>
        {error && <p className={styles.error} role="alert">{error}</p>}
        <form className={styles.form} onSubmit={handleSubmit}>
          <input
            type="email" name="email" id="email" placeholder="Email" aria-label="Email" className={styles.input}
            autoComplete="username"
            value={email} onChange={(e) => setEmail(e.target.value)} required
          />
          <input
            type="password" name="password" id="password" placeholder="Password" aria-label="Password" className={styles.input}
            autoComplete={isRegister ? 'new-password' : 'current-password'}
            value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8}
          />
          <button type="submit" className={styles.button} disabled={loading} aria-busy={loading}>
            {loading ? <><span className="mc-spinner" aria-hidden />Loading…</> : isRegister ? 'Create account' : 'Sign in'}
          </button>
        </form>
        <div className={styles.links}>
          <button className={styles.toggle} onClick={() => { setIsRegister(!isRegister); setError(''); }}>
            {isRegister ? 'Already have an account? Sign in' : "Don't have an account? Register"}
          </button>
          {!isRegister && (
            <button className={styles.toggle} onClick={() => setForgotMode(true)}>
              Forgot password?
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
